import { dispatchCopy, dispatchNumber, isArabic } from './dispatchCopy';

const EN = {
  page: {
    breadcrumb: ['Operations', 'Live jobs'], title: 'Live jobs', lead: 'Every booking currently in flight.',
    emptyTitle: 'Nothing in flight',
    emptyMsg: 'No booking is currently active. New ones appear here as soon as they are requested.'
  },
  scheduledPage: {
    breadcrumb: ['Operations', 'Scheduled'], title: 'Scheduled jobs',
    lead: 'Bookings placed for a future time slot.', emptyTitle: 'Nothing scheduled',
    emptyMsg: 'Scheduled bookings will appear here when customers create them.'
  },
  completedPage: {
    breadcrumb: ['Operations', 'Completed'], title: 'Completed jobs',
    lead: 'Finished and settled bookings.', emptyTitle: 'Nothing completed yet',
    emptyMsg: 'Finished jobs appear here once the provider closes them.'
  },
  cancelledPage: {
    breadcrumb: ['Operations', 'Cancelled'], title: 'Cancelled jobs',
    lead: 'Cancelled, expired, and refunded bookings with their recorded reasons.',
    emptyTitle: 'Nothing cancelled', emptyMsg: 'Cancelled and expired bookings appear here.'
  },
  reference: 'Reference', customer: 'Customer', provider: 'Provider', service: 'Service', area: 'Area',
  statusLabel: 'Status', reason: 'Reason', fare: 'Fare', time: 'Time',
  completedAt: 'Completed at', closedAt: 'Closed at', unknown: 'Unknown',
  unassigned: 'Unassigned', timedOut: 'Timed out', notRecorded: 'Not recorded',
  viewBooking: 'View booking', assignProvider: 'Assign provider', cancelBooking: 'Cancel booking',
  overview: 'Overview', review: 'Review', chat: 'Chat', close: 'Close', lifecycle: 'Lifecycle',
  bookingCreated: 'Booking created', paidUpFront: 'Paid up front', providerAssigned: 'Provider assigned',
  arrivedScene: 'Arrived on scene', workStarted: 'Work started', completedSettled: 'Completed & settled',
  cancelled: 'Cancelled', expiredEvent: 'Expired', refundedEvent: 'Refunded',
  direct: 'direct', standard: 'standard', scheduledType: 'scheduled', type: 'Type',
  scheduledFor: 'Scheduled slot', scheduleState: 'Schedule status',
  scheduleStatus: { scheduled: 'Scheduled', completed: 'Triggered', failed: 'Failed' },
  noReview: 'No review yet', noReviewMsg: 'The customer has not rated this job.',
  noMessages: 'No messages', noMessagesMsg: 'Chat opens once a provider is assigned.',
  exportCsv: 'Export CSV', preparing: 'Preparing…', openClassic: 'Open in classic', dismiss: 'Dismiss',
  assign: id => `Assign BK-${id}`, confirmAssignment: 'Confirm assignment', chooseProvider: 'Choose a provider…',
  unnamed: 'Unnamed', offline: 'offline', cancelTitle: id => `Cancel BK-${id}?`,
  cancelSub: 'The booking is closed and the cancellation reason is recorded.',
  cancellationReason: 'Cancellation reason', chooseReason: 'Choose a reason…',
  reasonRequired: 'A reason is required before cancelling.', cancel: 'Cancel', working: 'Working…',
  loadError: 'Could not load bookings.', assignError: 'Could not assign.', cancelError: 'Could not cancel.',
  assigned: (id, who) => `BK-${id} assigned to ${who}.`,
  cancelledNotice: (id, reason) => `BK-${id} cancelled — ${reason}.`,
  providerFallback: 'provider', customerFallback: 'Customer', serviceFallback: 'service',
  search: 'Search reference, customer, provider or area', filterService: 'Filter by service',
  allServices: 'Service: all', nothingShow: 'Nothing to show',
  showing: (from, to, count) => `Showing ${from}–${to} of ${count}`,
  sortedPage: 'sorted within this page', previousPage: 'Previous page', nextPage: 'Next page',
  rowsPerPage: 'Rows per page', perPage: n => `${n} / page`,
  age: minutes => minutes < 60 ? `${minutes}m` : minutes < 1440 ? `${(minutes / 60).toFixed(1)}h` : `${Math.round(minutes / 1440)}d`,
  reasons: {
    'Customer requested': 'Customer requested', 'No provider available': 'No provider available',
    'Duplicate booking': 'Duplicate booking', 'Vehicle not accessible': 'Vehicle not accessible',
    'Fraud suspected': 'Fraud suspected', Other: 'Other'
  }
};

const AR = {
  page: {
    breadcrumb: ['العمليات', 'الوظائف النشطة'], title: 'الوظائف النشطة', lead: 'جميع الحجوزات قيد التنفيذ حاليًا.',
    emptyTitle: 'لا توجد وظائف نشطة',
    emptyMsg: 'لا توجد حجوزات نشطة حاليًا. تظهر الحجوزات الجديدة هنا فور طلبها.'
  },
  scheduledPage: {
    breadcrumb: ['العمليات', 'المجدولة'], title: 'الحجوزات المجدولة',
    lead: 'الحجوزات المحجوزة لفترة زمنية مستقبلية.', emptyTitle: 'لا توجد حجوزات مجدولة',
    emptyMsg: 'ستظهر الحجوزات المجدولة هنا عندما ينشئها العملاء.'
  },
  completedPage: {
    breadcrumb: ['العمليات', 'المكتملة'], title: 'الحجوزات المكتملة',
    lead: 'الحجوزات التي اكتملت وتمت تسويتها.', emptyTitle: 'لا توجد حجوزات مكتملة',
    emptyMsg: 'ستظهر الحجوزات هنا بعد إكمال مقدم الخدمة لها.'
  },
  cancelledPage: {
    breadcrumb: ['العمليات', 'الملغاة'], title: 'الحجوزات الملغاة',
    lead: 'الحجوزات الملغاة والمنتهية والمستردة مع أسبابها المسجلة.',
    emptyTitle: 'لا توجد حجوزات ملغاة', emptyMsg: 'ستظهر الحجوزات الملغاة والمنتهية هنا.'
  },
  reference: 'الرقم المرجعي', customer: 'العميل', provider: 'مقدم الخدمة', service: 'الخدمة', area: 'المنطقة',
  statusLabel: 'الحالة', reason: 'السبب', fare: 'الأجرة', time: 'الوقت',
  completedAt: 'وقت الإكمال', closedAt: 'وقت الإغلاق', unknown: 'غير معروف',
  unassigned: 'غير معيّن', timedOut: 'انتهت المهلة', notRecorded: 'غير مسجل',
  viewBooking: 'عرض الحجز', assignProvider: 'تعيين مقدم خدمة', cancelBooking: 'إلغاء الحجز',
  overview: 'نظرة عامة', review: 'التقييم', chat: 'المحادثة', close: 'إغلاق', lifecycle: 'دورة الحجز',
  bookingCreated: 'تم إنشاء الحجز', paidUpFront: 'مدفوع مقدمًا', providerAssigned: 'تم تعيين مقدم الخدمة',
  arrivedScene: 'وصل إلى الموقع', workStarted: 'بدأ العمل', completedSettled: 'مكتمل وتمت التسوية',
  cancelled: 'ملغى', expiredEvent: 'انتهت صلاحيته', refundedEvent: 'تم رد المبلغ',
  direct: 'مباشر', standard: 'عادي', scheduledType: 'مجدول', type: 'النوع',
  scheduledFor: 'الفترة المجدولة', scheduleState: 'حالة الجدولة',
  scheduleStatus: { scheduled: 'مجدول', completed: 'تم التشغيل', failed: 'فشل' },
  noReview: 'لا يوجد تقييم بعد', noReviewMsg: 'لم يقيّم العميل هذه الوظيفة.',
  noMessages: 'لا توجد رسائل', noMessagesMsg: 'تُفتح المحادثة بعد تعيين مقدم خدمة.',
  exportCsv: 'تصدير CSV', preparing: 'جارٍ التحضير…', openClassic: 'فتح في النسخة الكلاسيكية', dismiss: 'إخفاء',
  assign: id => `تعيين الحجز BK-${id}`, confirmAssignment: 'تأكيد التعيين', chooseProvider: 'اختر مقدم خدمة…',
  unnamed: 'بلا اسم', offline: 'غير متصل', cancelTitle: id => `إلغاء الحجز BK-${id}؟`,
  cancelSub: 'سيُغلق الحجز ويُسجل سبب الإلغاء.',
  cancellationReason: 'سبب الإلغاء', chooseReason: 'اختر سببًا…',
  reasonRequired: 'يجب اختيار سبب قبل الإلغاء.', cancel: 'إلغاء', working: 'جارٍ التنفيذ…',
  loadError: 'تعذر تحميل الحجوزات.', assignError: 'تعذر تعيين مقدم الخدمة.', cancelError: 'تعذر إلغاء الحجز.',
  assigned: (id, who) => `تم تعيين الحجز BK-${id} إلى ${who}.`,
  cancelledNotice: (id, reason) => `أُلغي الحجز BK-${id} — ${reason}.`,
  providerFallback: 'مقدم الخدمة', customerFallback: 'العميل', serviceFallback: 'الخدمة',
  search: 'ابحث بالرقم المرجعي أو العميل أو مقدم الخدمة أو المنطقة', filterService: 'تصفية حسب الخدمة',
  allServices: 'الخدمة: الكل', nothingShow: 'لا توجد نتائج',
  showing: (from, to, count) => `عرض ${from}–${to} من ${count}`,
  sortedPage: 'الترتيب داخل هذه الصفحة', previousPage: 'الصفحة السابقة', nextPage: 'الصفحة التالية',
  rowsPerPage: 'عدد الصفوف في الصفحة', perPage: n => `${n} / صفحة`,
  age: minutes => minutes < 60
    ? `${dispatchNumber(minutes, 'ar')}د`
    : minutes < 1440
      ? `${dispatchNumber(minutes / 60, 'ar', { maximumFractionDigits: 1 })}س`
      : `${dispatchNumber(Math.round(minutes / 1440), 'ar')}ي`,
  reasons: {
    'Customer requested': 'بطلب العميل', 'No provider available': 'لا يوجد مقدم خدمة متاح',
    'Duplicate booking': 'حجز مكرر', 'Vehicle not accessible': 'تعذر الوصول إلى المركبة',
    'Fraud suspected': 'اشتباه في احتيال', Other: 'أخرى'
  }
};

export const liveJobsCopy = locale => ({
  ...(isArabic(locale) ? AR : EN),
  rtl: isArabic(locale),
  status: dispatchCopy(locale).status
});
